import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsService } from '@/services/notifications.service';
import { useAuthStore } from '@/stores/auth';

const NOTIFICATIONS_KEY = ['notifications'];
const UNREAD_COUNT_KEY = ['notifications', 'unread-count'];

export function useNotifications(page = 1, limit = 30) {
  const { user, isAuthenticated } = useAuthStore();
  const canRead = isAuthenticated && !!user?.id;
  const query = useQuery({
    queryKey: [...NOTIFICATIONS_KEY, user?.id, page, limit],
    queryFn: () => notificationsService.getAll(page, limit),
    enabled: canRead,
    refetchInterval: 15000,
    staleTime: 5000,
    refetchOnWindowFocus: true,
  });
  return { ...query, data: canRead && !query.isError ? query.data : undefined };
}

export function useUnreadCount() {
  const { user, isAuthenticated } = useAuthStore();
  const canRead = isAuthenticated && !!user?.id;
  const query = useQuery({
    queryKey: [...UNREAD_COUNT_KEY, user?.id],
    queryFn: () => notificationsService.getUnreadCount(),
    enabled: canRead,
    refetchInterval: 10000,
    staleTime: 3000,
    refetchOnWindowFocus: true,
  });
  return { ...query, data: canRead && !query.isError ? query.data : undefined };
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });
}
